import React, { useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { compose } from "redux";
import { motion } from "framer-motion";

import { actions as topikActions } from "../../../../modules/topik";
import { useTopikMotion } from "../topikMotion";
import { commitHaptic } from "../haptics";
import AnalysisFrame from "./AnalysisFrame";
import AnalysisRecall, { chosenPairCards } from "./AnalysisRecall";
import TopikScaleSlider from "./TopikScaleSlider";

const SCALE_MIN = 1;
const SCALE_MAX = 5;
const SHORT_CIRCUIT_MAX = 2;

const TopikSeriousnessView = ({ t, session, topikStatus, gameClosed, dispatch }) => {
  const { reduce } = useTopikMotion();
  const [value, setValue] = useState(session.seriousness || null);
  // The retreat gate (§6.2a): a 1-2 rating opens a confirm before it ends the run.
  const [confirming, setConfirming] = useState(false);

  const submit = (rating) => {
    if (rating === null || topikStatus.submitting || gameClosed) return;
    commitHaptic();
    dispatch(topikActions.analysis.seriousness.request({ seriousness: rating }));
  };

  const handleContinue = () => {
    if (value === null || topikStatus.submitting || gameClosed) return;
    if (value <= SHORT_CIRCUIT_MAX) {
      setConfirming(true);
    } else {
      submit(value);
    }
  };

  return (
    <AnalysisFrame
      t={t}
      title={t("topik-seriousness-view.title")}
      canBack
      submitting={topikStatus.submitting}
      gameClosed={gameClosed}
      error={topikStatus.error}
      onBack={() => dispatch(topikActions.analysis.back.request())}
    >
      <div className="topik-analysis__step">
        {/* The question rates "this observation" - carry the player's own words onto the screen. */}
        <AnalysisRecall
          entries={[{
            key: "observation",
            label: t("topik-analysis.recall.observation"),
            value: session.observation,
            cards: chosenPairCards(session),
          }]}
        />
        <p className="topik-analysis__question">{t("topik-seriousness-view.question")}</p>
        {/* A line to drag along rather than five buttons; the 1-5 number is kept underneath. */}
        <TopikScaleSlider
          value={value}
          min={SCALE_MIN}
          max={SCALE_MAX}
          onChange={setValue}
          lowLabel={t("topik-seriousness-view.low-label")}
          highLabel={t("topik-seriousness-view.high-label")}
          ariaLabel={t("topik-seriousness-view.slider-label")}
          readout={(n) => t("topik-seriousness-view.value", { value: n, max: SCALE_MAX })}
          unsetLabel={t("topik-seriousness-view.unset")}
          disabled={topikStatus.submitting}
        />

        {confirming ? (
          <motion.div
            className="topik-analysis__confirm"
            role="alertdialog"
            aria-label={t("topik-seriousness-view.confirm.title")}
            initial={reduce ? false : { opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduce ? 0 : 0.15 }}
          >
            <p className="topik-analysis__confirm-title">{t("topik-seriousness-view.confirm.title")}</p>
            <p className="topik-analysis__confirm-message">{t("topik-seriousness-view.confirm.message")}</p>
            <div className="topik-analysis__confirm-actions">
              <button
                type="button"
                className="topik-view__submit-button topik-view__submit-button--subtle topik-analysis__confirm-revise"
                onClick={() => {
                  setConfirming(false);
                  dispatch(topikActions.analysis.back.request());
                }}
                disabled={topikStatus.submitting}
              >
                {t("topik-seriousness-view.confirm.revise")}
              </button>
              <button
                type="button"
                className="topik-view__submit-button topik-analysis__confirm-finish"
                onClick={() => submit(value)}
                disabled={topikStatus.submitting || gameClosed}
              >
                {t("topik-seriousness-view.confirm.finish")}
              </button>
              <button
                type="button"
                className="topik-analysis__confirm-cancel"
                onClick={() => setConfirming(false)}
                disabled={topikStatus.submitting}
              >
                {t("topik-seriousness-view.confirm.cancel")}
              </button>
            </div>
          </motion.div>
        ) : (
          <button
            type="button"
            className="topik-view__submit-button"
            disabled={value === null || topikStatus.submitting || gameClosed}
            onClick={handleContinue}
          >
            {t("topik-seriousness-view.submit")}
          </button>
        )}
      </div>
    </AnalysisFrame>
  );
};

TopikSeriousnessView.propTypes = {
  t: PropTypes.func.isRequired,
  session: PropTypes.object.isRequired,
  topikStatus: PropTypes.object.isRequired,
  gameClosed: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
};

TopikSeriousnessView.defaultProps = {
  gameClosed: false,
};

const mapStateToProps = (state) => ({
  session: state.topik.session,
  topikStatus: state.topik.status,
});

export default compose(withTranslation("views"), connect(mapStateToProps))(TopikSeriousnessView);
